import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { hasPermission } from 'utils/utils';
import { TABLE_HEIGHT } from 'constants/ui';
import { FormField } from 'types/formFieldTypes';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { FIELD_DELETED_ID } from 'constants/userActivities';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';

interface FormFieldsTableProps {
    formFields: FormField[];
    setFormFields: (formFields: FormField[]) => void;
    handleEditClick: (formField: FormField) => void;
    handleAddClick: () => void;
    loading: boolean;
}

const FormFieldsTable: React.FC<FormFieldsTableProps> = ({ formFields, setFormFields, handleEditClick, handleAddClick, loading }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const hasFormFieldsWrite = hasPermission(tokenScopeList, orgId, 'o', 'fields:w');

    const { t } = useTranslation();

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'name',
            label: t('formFields.table.name'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            dataIndex: 'placeholder',
            label: t('formFields.table.placeholder'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.placeholder || '').localeCompare(b.placeholder || ''),
        },
    ];

    const handleDeleteFormField = async (formField: FormField) => {
        try {
            await patchRequest(`/orgs/${orgId}/formField/${formField.id}`, {
                status: 0
            });
            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: FIELD_DELETED_ID,
                details: formField.name
            });

            // Re-fetch the updated list of fields
            const updatedFields = await getRequest(`/orgs/${orgId}/formField`);
            const filteredUpdatedFormFields = updatedFields.data.filter((field: FormField) => field.status === 1);
            setFormFields(filteredUpdatedFormFields);
            notification.success({
                message: t('notification.success'),
                description: t('formFields.notification.deleteSuccess'),
                placement: 'bottomRight',
            });
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('formFields.ƒnotification.deleteError'),
                placement: 'bottomRight',
            });
        }
    };

    // Table properties
    const opTableProps: IOpTableProps = {
        dataSource: formFields,
        columns: columns,
        rowActions: {
            onEditClick: (formFields: FormField) => handleEditClick(formFields),
            onDeleteClick: hasFormFieldsWrite ? handleDeleteFormField : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('formFields.itemName') }),
            deleteModalContent: ({ record }) => t('confirmDelete.message', { name: record.name }),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasFormFieldsWrite,
        allowAddition: hasFormFieldsWrite ? {
            itemName: t('formFields.itemName'),
            onClick: () => handleAddClick(),
        } : false,
        loading: loading,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return (
        <OpTable {...opTableProps} />
    );
};

export default FormFieldsTable;
